import { useFormik } from "formik";
import React, { useCallback, useState } from "react";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Checkbox from "../../../../../../components/Checkbox";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { IOperacoesPatioCancela } from "../../types/types";
import formValidator from "../validators/formValidator";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";
import { FrontendNotification } from "../../../../../../shared/Notification";

interface FormValues {
  numero_cancela: string;
  tipo_cancela: string;
  ip_camera_dianteira: string;
  ip_controladora: string;
  atuar_na_cancela: boolean;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  isSpe?: boolean;
  selectedRow?: IOperacoesPatioCancela;
  onConfirm: () => void;
  onClose: () => void;
}

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  const handleSubmit = useCallback(
    async (values: FormValues, row?: IOperacoesPatioCancela) => {
      try {
        setLoading(true);

        const userId = user?.id;

        const body = {
          id_operacao_patio_cancela: row?.id_operacao_patio_cancela,
          numero_cancela: Number(values.numero_cancela),
          tipo_cancela: values.tipo_cancela === "ENTRADA" ? 1 : (values.tipo_cancela === "SAIDA" ? 2 : values.tipo_cancela),
          ip_camera_dianteira: values.ip_camera_dianteira,
          ip_controladora: values.ip_controladora,
          atuar_na_cancela: values.atuar_na_cancela,
          ativo: true,
          id_usuario_historico: userId,
          status: 1,
        };

        if (props.isSpe) {
          if (props.isEdit) {
            await api.post("/atualizar/cancelasSpe", body).catch(() =>
              api.post("/editar/cancelasSpe", body)
            );
            FrontendNotification("Cancela SPE atualizada com sucesso!", "success");
          } else {
            await api.post("/cadastrar/cancelasSpe", body);
            FrontendNotification("Cancela SPE cadastrada com sucesso!", "success");
          }
        } else {
          if (props.isEdit) {
            await api.post("/atualizar/cancelas", body).catch(() =>
              api.post("/editar/cancelas", body)
            );
            FrontendNotification("Cancela atualizada com sucesso!", "success");
          } else {
            await api.post("/cadastrar/cancelas", body);
            FrontendNotification("Cancela cadastrada com sucesso!", "success");
          }
        }

        setLoading(false);
        props.onConfirm();
      } catch (error: any) {
        setLoading(false);
        const msg = error?.response?.data?.error_msg || "Ocorreu um erro ao salvar a cancela.";
        FrontendNotification(msg, "error");
      }
    },
    [props, user]
  );

  const initialValues: FormValues = {
    numero_cancela: props.selectedRow?.numero_cancela ? String(props.selectedRow.numero_cancela) : "",
    tipo_cancela: String(props.selectedRow?.tipo_cancela) === "1" ? "ENTRADA" : (String(props.selectedRow?.tipo_cancela) === "2" ? "SAIDA" : String(props.selectedRow?.tipo_cancela || "")),
    ip_camera_dianteira: props.selectedRow?.ip_camera_dianteira || "",
    ip_controladora: props.selectedRow?.ip_controladora || "",
    atuar_na_cancela: props.selectedRow?.atuar_na_cancela ?? true,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: formValidator,
    onSubmit: (values: FormValues) => handleSubmit(values, props.selectedRow),
    enableReinitialize: true,
  });

  return (
    <>
      <Loading loading={loading} skeletonFields={5} skeletonColumns={2} />
      <div className="grid grid-cols-2 gap-3 mb-4 p-5">
        <div>
          <InputCustom
            id="numero_cancela"
            title="Número Cancela"
            placeholder="Número Cancela"
            onChange={formik.handleChange("numero_cancela")}
            onBlur={() => formik.setFieldTouched("numero_cancela", true)}
            value={formik.values.numero_cancela}
            error={formik.errors.numero_cancela}
            touched={formik.touched.numero_cancela}
            disabled={props.isView}
            type="number"
          />
        </div>
        <div>
          <SelectCustom
            id="tipo_cancela"
            title="Tipo Cancela"
            data={[
              { value: "ENTRADA", label: "ENTRADA" },
              { value: "SAIDA", label: "SAIDA" },
            ]}
            onChange={(selectedOption: any) =>
              formik.setFieldValue("tipo_cancela", selectedOption.value)
            }
            touched={formik.touched.tipo_cancela}
            error={formik.errors.tipo_cancela}
            value={formik.values.tipo_cancela}
            disabled={props.isView}
          />
        </div>
        <div>
          <InputCustom
            id="ip_camera_dianteira"
            title="IP Câmera Dianteira"
            placeholder="IP Câmera Dianteira"
            onChange={formik.handleChange("ip_camera_dianteira")}
            onBlur={() => formik.setFieldTouched("ip_camera_dianteira", true)}
            value={formik.values.ip_camera_dianteira}
            error={formik.errors.ip_camera_dianteira}
            touched={formik.touched.ip_camera_dianteira}
            disabled={props.isView}
          />
        </div>
        <div>
          <InputCustom
            id="ip_controladora"
            title="IP Controladora"
            placeholder="IP Controladora"
            onChange={formik.handleChange("ip_controladora")}
            onBlur={() => formik.setFieldTouched("ip_controladora", true)}
            value={formik.values.ip_controladora}
            error={formik.errors.ip_controladora}
            touched={formik.touched.ip_controladora}
            disabled={props.isView}
          />
        </div>
        <div className="flex items-center mt-6">
          <Checkbox
            id="atuar_na_cancela"
            title="Atuar na Cancela"
            checked={formik.values.atuar_na_cancela}
            onChecked={() => formik.setFieldValue("atuar_na_cancela", !formik.values.atuar_na_cancela)}
          />
        </div>
      </div>

      <div className="w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={props.onClose}
          id="btnCancel"
        >
          Cancelar
        </button>
        {!props.isView && (
          <button
            type="button"
            className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
            onClick={() => formik.handleSubmit()}
            id="btnSave"
          >
            Salvar
          </button>
        )}
      </div>
    </>
  );
};

export default Form;
